/** Sorting, grouping and searching My contacts, like a phone's contacts app. */

type Named = Readonly<{ firstName: string; lastName?: string | null | undefined; phoneNumber?: string | null | undefined; emailAddress?: string | null | undefined }>

const collator = new Intl.Collator('en-GB', { sensitivity: 'base', numeric: true })

/** Accents and case don't matter when sorting, grouping or searching: Martín sorts with Martin. */
export const fold = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

/** First name, then surname, as most phones list contacts: the surname is optional, the first name never is. */
const sortName = (person: Named) => [person.firstName.trim(), person.lastName?.trim() ?? '']

export function compareContacts(first: Named, second: Named): number {
  const [a1, a2] = sortName(first)
  const [b1, b2] = sortName(second)
  return collator.compare(a1 ?? '', b1 ?? '') || collator.compare(a2 ?? '', b2 ?? '')
}

/** The heading a contact sits under: the first letter of their first name, or # for anything else. */
export function letterFor(person: Named): string {
  const initial = fold(sortName(person)[0] ?? '').charAt(0).toUpperCase()
  return /^[A-Z]$/.test(initial) ? initial : '#'
}

/** Two letters for the avatar: first name and surname initials. */
export function initialsFor(person: Named): string {
  return [person.firstName, person.lastName].map((part) => part?.trim().charAt(0) ?? '').join('').toUpperCase() || '?'
}

const digits = (text: string) => text.replace(/\D/g, '')

/** What a search compares, worked out once per contact rather than on every keystroke. */
export type SearchKey = Readonly<{ text: string; digits: string }>

export function searchKey(person: Named): SearchKey {
  return { text: fold([person.firstName, person.lastName, person.emailAddress].filter(Boolean).join(' ')), digits: digits(person.phoneNumber ?? '') }
}

/** A query, folded once, to test against many keys. */
export type SearchQuery = Readonly<{ text: string; digits: string }> | null

export function searchQuery(query: string): SearchQuery {
  const text = fold(query.trim())
  return text ? { text, digits: digits(query).replace(/^0+/, '') } : null
}

/**
 * Matches the name or email, or the phone. A phone search ignores spaces, and
 * a leading 0 or country code, so 07700 900123 finds +44 7700 900123.
 */
export function matchesKey(key: SearchKey, query: SearchQuery): boolean {
  if (!query) return true
  return key.text.includes(query.text) || (query.digits.length >= 3 && key.digits.includes(query.digits))
}

export function matchesSearch(person: Named, query: string): boolean {
  return matchesKey(searchKey(person), searchQuery(query))
}

/** Groups already-sorted contacts by heading letter, in A–Z order with # last. */
export function groupByLetter<T extends Named>(people: readonly T[]): Array<Readonly<{ letter: string; people: T[] }>> {
  const groups = new Map<string, T[]>()
  for (const person of [...people].sort(compareContacts)) {
    const letter = letterFor(person)
    groups.set(letter, [...(groups.get(letter) ?? []), person])
  }
  return [...groups.entries()]
    .sort(([first], [second]) => (first === '#' ? 1 : second === '#' ? -1 : first.localeCompare(second)))
    .map(([letter, members]) => ({ letter, people: members }))
}
